/**
 * Roadmap "practice started" sessions — aggregate started snippets that are not due
 * today into a single study queue (track-wide or scoped to one cookbook/node).
 */

import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { isSnippetStarted } from '../components/Roadmap/RoadmapList';
import SpacedRepetition from '../components/SRS/SpacedRepetition';
import { loadSnippetsByIds, uniqueSnippetIdsInOrder } from '../services/roadmapService';

async function loadCookbookCardsFromFirestore(userId, cookbookId) {
  const cardsSnap = await getDocs(
    collection(db, `users/${userId}/decks/${cookbookId}/cards`)
  );
  return cardsSnap.docs.map((cardDoc) => ({ id: cardDoc.id, ...cardDoc.data() }));
}

/**
 * @param {string} snippetId
 * @param {Record<string, unknown>} snippetStatsById
 * @param {Set<string>} dueSnippetIds
 */
export function isSnippetStartedNotDue(snippetId, snippetStatsById, dueSnippetIds) {
  if (!snippetId) return false;
  if (!isSnippetStarted(snippetStatsById?.[snippetId])) return false;
  if (dueSnippetIds instanceof Set && dueSnippetIds.has(snippetId)) return false;
  return true;
}

/**
 * Ordered, de-duped snippet ids (track or single-node scope) that are started but not due.
 *
 * @param {Array<{ id?: string, snippetIds?: string[] }>} nodes
 * @param {Record<string, unknown>} snippetStatsById
 * @param {Set<string>} dueSnippetIds
 * @param {string | null | undefined} [scopeNodeId] — when set, only that cookbook/node
 */
export function collectStartedNotDueSnippetIds(
  nodes,
  snippetStatsById,
  dueSnippetIds,
  scopeNodeId = null
) {
  const nodeList = Array.isArray(nodes) ? nodes : [];
  const scoped = scopeNodeId
    ? nodeList.filter((node) => node?.id === scopeNodeId)
    : nodeList;

  const orderedIds = [];
  const seen = new Set();

  for (const node of scoped) {
    const ids = uniqueSnippetIdsInOrder(node?.snippetIds || []);
    for (const id of ids) {
      if (!id || seen.has(id)) continue;
      if (!isSnippetStartedNotDue(id, snippetStatsById, dueSnippetIds)) continue;
      seen.add(id);
      orderedIds.push(id);
    }
  }

  return orderedIds;
}

/** Total + started-not-due snippets for a track (de-duped across cookbooks). */
export function computeTrackSnippetStartedNotDue(nodes, snippetStatsById, dueSnippetIds) {
  if (!Array.isArray(nodes) || nodes.length === 0) {
    return { total: 0, startedNotDue: 0 };
  }
  const seen = new Set();
  let total = 0;
  let startedNotDue = 0;
  for (const node of nodes) {
    const ids = node?.snippetIds;
    if (!Array.isArray(ids)) continue;
    for (const id of ids) {
      if (!id || seen.has(id)) continue;
      seen.add(id);
      total += 1;
      if (isSnippetStartedNotDue(id, snippetStatsById, dueSnippetIds)) {
        startedNotDue += 1;
      }
    }
  }
  return { total, startedNotDue };
}

/**
 * Load deck cards for a practice-started session, preserving track/cookbook order.
 *
 * @param {string} userId
 * @param {{ trackId?: string }} track
 * @param {Array<{ id: string, title?: string, snippetIds?: string[] }>} nodes
 * @param {string[]} snippetIdsOrdered
 * @param {(track: object, node: object, snippets: object[]) => Promise<{ cookbookId: string, cards?: object[] } | string>} resolveCookbookId
 */
export async function loadRoadmapPracticeStartedCards(
  userId,
  track,
  nodes,
  snippetIdsOrdered,
  resolveCookbookId
) {
  const uid = typeof userId === 'string' ? userId.trim() : '';
  if (!uid || !Array.isArray(snippetIdsOrdered) || snippetIdsOrdered.length === 0) {
    return [];
  }

  const targetSet = new Set(snippetIdsOrdered);
  const nodeList = Array.isArray(nodes) ? nodes : [];
  const srs = new SpacedRepetition(uid);
  /** @type {Array<Record<string, unknown>>} */
  const cards = [];

  for (const node of nodeList) {
    const nodeSnippetIds = uniqueSnippetIdsInOrder(node?.snippetIds || []);
    const hasMatch = nodeSnippetIds.some((id) => targetSet.has(id));
    if (!hasMatch) continue;

    const snippets = await loadSnippetsByIds(node.snippetIds || []);
    if (snippets.length === 0) continue;

    const session = await resolveCookbookId(track, node, snippets);
    const cookbookId =
      session && typeof session === 'object' ? session.cookbookId : session;
    if (!cookbookId) continue;

    const progressRows = await srs.getDeckCardProgressList(cookbookId);
    const nodeCards = session && typeof session === 'object' && Array.isArray(session.cards)
      ? session.cards
      : null;

    const sourceCards = nodeCards || (await loadCookbookCardsFromFirestore(uid, cookbookId));

    for (const card of sourceCards) {
      const snippetId = card.roadmapSnippetId != null ? String(card.roadmapSnippetId).trim() : '';
      if (!snippetId || !targetSet.has(snippetId)) continue;

      cards.push({
        ...card,
        deckId: cookbookId,
        roadmapNodeId: node.id,
        roadmapNodeTitle: node.title || '',
        progress: progressRows.find((row) => row.cardId === card.id) || null
      });
    }
  }

  const orderMap = new Map(snippetIdsOrdered.map((id, index) => [id, index]));
  cards.sort((a, b) => {
    const aId = a.roadmapSnippetId != null ? String(a.roadmapSnippetId).trim() : '';
    const bId = b.roadmapSnippetId != null ? String(b.roadmapSnippetId).trim() : '';
    return (orderMap.get(aId) ?? 9999) - (orderMap.get(bId) ?? 9999);
  });

  return cards;
}
